/**
 * Toque visible (Rumbo móvil): mientras el dedo está sobre algo que se puede
 * pulsar, ese elemento lleva `data-tocando` y `index.html` lo tiñe de verde
 * suave. No se usa `:active` porque en Android no siempre se activa (react-aria
 * gestiona la pulsación) y no llega a verse. Al empezar a desplazar la lista el
 * navegador cancela el puntero y el tinte se quita.
 */

export const SELECTOR_TOCABLE = [
  'button',
  'a[href]',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="switch"]',
].join(', ');

/** Espera antes de teñir, para que un desplazamiento no deje un destello. */
export const RETRASO_TOQUE_MS = 60;

/** Lo que dura el tinte tras levantar el dedo. */
export const DESTELLO_MS = 120;

/** El elemento pulsable más cercano bajo el dedo, si se puede tocar. */
export function elementoTocable(destino: EventTarget | null): Element | null {
  if (!(destino instanceof Element)) {
    return null;
  }
  const elemento = destino.closest(SELECTOR_TOCABLE);
  if (
    !elemento ||
    elemento.hasAttribute('disabled') ||
    elemento.getAttribute('aria-disabled') === 'true'
  ) {
    return null;
  }
  return elemento;
}

/** Instala los oyentes una sola vez; devuelve cómo quitarlos (tests). */
export function instalarToque(doc: Document = document): () => void {
  let actual: Element | null = null;
  let espera: ReturnType<typeof setTimeout> | null = null;

  let pendiente: Element | null = null;

  const soltar = () => {
    if (espera != null) {
      clearTimeout(espera);
      espera = null;
    }
    pendiente = null;
    actual?.removeAttribute('data-tocando');
    actual = null;
  };

  // Un toque rápido también se ve: el tinte dura un instante tras levantar
  // el dedo, aunque no hubiera dado tiempo a ponerlo.
  const levantar = () => {
    const elemento = actual ?? pendiente;
    soltar();
    if (!elemento) {
      return;
    }
    elemento.setAttribute('data-tocando', '');
    setTimeout(() => elemento.removeAttribute('data-tocando'), DESTELLO_MS);
  };

  const tocar = (e: PointerEvent) => {
    soltar();
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') {
      return;
    }
    const elemento = elementoTocable(e.target);
    if (!elemento) {
      return;
    }
    pendiente = elemento;
    espera = setTimeout(() => {
      espera = null;
      pendiente = null;
      actual = elemento;
      elemento.setAttribute('data-tocando', '');
    }, RETRASO_TOQUE_MS);
  };

  const opciones = { capture: true, passive: true } as const;
  doc.addEventListener('pointerdown', tocar, opciones);
  doc.addEventListener('pointerup', levantar, opciones);
  doc.addEventListener('pointercancel', soltar, opciones);
  doc.addEventListener('scroll', soltar, opciones);
  return () => {
    soltar();
    doc.removeEventListener('pointerdown', tocar, opciones);
    doc.removeEventListener('pointerup', levantar, opciones);
    doc.removeEventListener('pointercancel', soltar, opciones);
    doc.removeEventListener('scroll', soltar, opciones);
  };
}
